import React, { ChangeEvent } from 'react';
import { Text } from '@mantine/core';
import { IconFolders, IconSearch } from '@tabler/icons-react';

import { DebouncedInput, When } from '../common';
import { filterTrainingMaterialsBySearch } from '../../helpers/trainingHelpers';
import { TrainingCategory, TrainingMaterial } from '../../types/training';
import { TrainingAudienceBadges } from './TrainingAudienceBadges';
import { TrainingMaterialCard } from './TrainingMaterialCard';

type Props = {
    category: TrainingCategory;
    materials: TrainingMaterial[];
    searchValue: string;
    isSuperAdmin: boolean;
    actionLoadingId: number | null;
    onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onView: (material: TrainingMaterial) => void;
    onDownload: (material: TrainingMaterial) => void;
    onEdit: (material: TrainingMaterial) => void;
    onDelete: (material: TrainingMaterial) => void;
};

export function TrainingCategoryMaterialsPanel({
    category,
    materials,
    searchValue,
    isSuperAdmin,
    actionLoadingId,
    onSearchChange,
    onView,
    onDownload,
    onEdit,
    onDelete,
}: Props) {
    const filteredMaterials = filterTrainingMaterialsBySearch(
        materials,
        searchValue,
    );

    return (
        <>
            <div className="sticky top-24 z-20 -mx-4 -mt-4 mb-4 bg-white px-4 pt-4 pb-4 border-b border-gray-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <IconFolders size={22} className="shrink-0" />
                        <span className="text-lg font-medium">
                            {category.name}
                        </span>
                    </div>
                    <DebouncedInput
                        leftSection={<IconSearch size={16} />}
                        placeholder="Search materials"
                        value={searchValue}
                        onChange={onSearchChange}
                        className="w-full sm:w-64 shrink-0"
                        size="sm"
                    />
                </div>
                <When condition={Boolean(category.description)}>
                    <Text size="sm" c="dimmed" mb={8}>
                        {category.description}
                    </Text>
                </When>
                <TrainingAudienceBadges audiences={category.audiences} />
            </div>

            <When
                condition={materials.length === 0}
                fallback={
                    <When
                        condition={filteredMaterials.length === 0}
                        fallback={
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                                {filteredMaterials.map(material => (
                                    <TrainingMaterialCard
                                        key={material.id}
                                        material={material}
                                        isSuperAdmin={isSuperAdmin}
                                        actionLoading={
                                            actionLoadingId === material.id
                                        }
                                        onView={onView}
                                        onDownload={onDownload}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                    />
                                ))}
                            </div>
                        }
                    >
                        <div className="py-12 text-center text-gray-500">
                            No materials match your search
                        </div>
                    </When>
                }
            >
                <div className="py-12 text-center text-gray-500">
                    No materials in this category yet
                </div>
            </When>
        </>
    );
}
